import { useEffect, useRef, useState } from 'react';
import { useScrollPosition } from '../contexts/ScrollPositionContext';
import { getFilteredWills, Will } from '../services/api';
import InstagramWillCard from '../components/InstagramWillCard.tsx';
import '../styles/screen.css';

const Feed = () => {
  const [loadedWills, setLoadedWills] = useState<Will[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const { getScrollPosition, saveScrollPosition } = useScrollPosition();
  const scrollRestored = useRef(false);
  const feedKey = 'feedScroll';
  
  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition(feedKey, window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [saveScrollPosition]);
  
  // Restore scroll position on mount
  useEffect(() => {
    if (!scrollRestored.current) {
      const savedPosition = getScrollPosition(feedKey);
      
      if (savedPosition > 0) {
        // Use a small timeout to ensure content is loaded before scrolling
        const timer = setTimeout(() => {
          window.scrollTo(0, savedPosition);
          scrollRestored.current = true;
          console.log('Restored feed scroll position to:', savedPosition);
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [getScrollPosition]);
  
  // Load initial wills
  useEffect(() => {
    loadInitialWills();
  }, []);
  
  const loadInitialWills = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      const response = await getFilteredWills({
        size: 10
      });

      if (!response || !response.content) {
        setHasMore(false);
        return;
      }

      const newWills = response.content;
      
      if (newWills.length < 10) {
        setHasMore(false);
      }

      if (newWills.length > 0) {
        setCursor(newWills[newWills.length - 1].createdAt);
        
        // Check if we have any existing wills to preserve liked status
        setLoadedWills(prevWills => {
          if (prevWills.length === 0) {
            return newWills;
          }
          
          const existingWillsMap = new Map(
            prevWills.map(will => [will.id, will])
          );
          
          return newWills.map(newWill => {
            const existingWill = existingWillsMap.get(newWill.id);
            if (existingWill) {
              // If this will already exists in our state, preserve its liked status
              return {
                ...newWill,
                liked: existingWill.liked,
                likeCount: existingWill.liked !== newWill.liked 
                  ? (existingWill.liked ? Math.max(newWill.likeCount, existingWill.likeCount) : newWill.likeCount) 
                  : newWill.likeCount
              };
            }
            return newWill;
          });
        });
      }
    } catch (error) {
      console.error('Error loading wills:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };
  
  const loadMoreWills = async () => {
    if (loading || !hasMore) return;
    
    try {
      setLoading(true);
      const response = await getFilteredWills({
        cursor,
        size: 10
      });

      if (!response || !response.content) {
        setHasMore(false);
        return;
      }

      const newWills = response.content;
      
      if (newWills.length < 10) {
        setHasMore(false);
      }

      if (newWills.length > 0) {
        setCursor(newWills[newWills.length - 1].createdAt);
        
        // Preserve the liked status from existing wills
        setLoadedWills(prevWills => {
          const existingWillsMap = new Map(
            prevWills.map(will => [will.id, will])
          );
          
          const updatedNewWills = newWills.map(newWill => {
            const existingWill = existingWillsMap.get(newWill.id);
            if (existingWill) {
              // If this will already exists in our state, preserve its liked status
              return {
                ...newWill,
                liked: existingWill.liked,
                likeCount: existingWill.liked !== newWill.liked 
                  ? (existingWill.liked ? Math.max(newWill.likeCount, existingWill.likeCount) : newWill.likeCount) 
                  : newWill.likeCount
              };
            }
            return newWill;
          });
          
          return [...prevWills, ...updatedNewWills];
        });
      }
    } catch (error) {
      console.error('Error loading wills:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };
  
  // Set up intersection observer for infinite scrolling
  const observer = useRef<IntersectionObserver>();
  const lastWillElementRef = (node: HTMLElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreWills();
      }
    });
    
    if (node) observer.current.observe(node);
  };
  
  const handleLikeToggle = (willId: string, isLiked: boolean) => {
    // Update local state for optimistic UI updates
    setLoadedWills(prevWills => 
      prevWills.map(will => 
        will.id === willId 
          ? { 
              ...will, 
              liked: isLiked,
              likeCount: isLiked ? (will.likeCount + 1) : Math.max(0, will.likeCount - 1)
            } 
          : will
      )
    );
  };
  
  return (
    <div className="w-full max-w-[470px] mx-auto px-2 py-6 sm:px-0 lg:max-w-[470px] xl:max-w-[470px] lg:ml-[270px] lg:mr-[270px]">
      <div className="feed">
        {initialLoading ? (
          <div className="flex flex-col space-y-4">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="flex items-center p-3">
                  <div className="h-8 w-8 rounded-full bg-gray-200 mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
                <div className="aspect-square bg-gray-200"></div>
                <div className="p-3">
                  <div className="h-5 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {loadedWills.map((will, index) => (
              <div 
                key={will.id}
                ref={index === loadedWills.length - 1 ? lastWillElementRef : null}
              >
                <InstagramWillCard
                  id={will.id}
                  title={will.title}
                  description={will.description}
                  wanterName={will.wanterName || ''}
                  wanterId={will.wanterId}
                  category={will.category}
                  location={will.location || ''}
                  reward={will.budget ? `${will.budget}` : null}
                  budget={will.budget}
                  currency={will.currency}
                  media={will.media || []}
                  createdAt={will.createdAt}
                  dueDate={will.fulfillmentTime}
                  fulfillmentTimeType={null}
                  recurrencePattern={null}
                  onLikeToggle={handleLikeToggle}
                  isLiked={will.liked}
                  likeCount={will.likeCount}
                  commentCount={will.commentCount}
                  offerCount={will.offerCount}
                  wanterProfilePicture={will.wanterProfilePicture}
                />
              </div>
            ))}
            
            {loading && !initialLoading && (
              <div className="flex justify-center py-4">
                <div className="h-8 w-8 rounded-full border-2 border-black border-t-transparent animate-spin"></div>
              </div>
            )}
            
            {!loading && !hasMore && loadedWills.length > 0 && (
              <div className="text-center text-gray-500 py-6">
                That's all for now! Check back later for more wants.
              </div>
            )}
            
            {!loading && loadedWills.length === 0 && (
              <div className="text-center py-12">
                <div className="text-2xl mb-2">😢 No wants found</div>
                <p className="text-gray-500">
                  Be the first to post what you want!
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Feed;
